import {
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import React, { forwardRef, useMemo } from "react";

export type IconComponent = React.ComponentType<{
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}>;

export type UltimateButtonProps = Omit<ButtonProps, "variant" | "color"> & {
  variant?: "contained" | "outlined";
  text: string;
  color?: "brand" | "brandDanger" | "brandSuccess" | "white" | "gray" | "black";
  Icon?: IconComponent;
  IconRight?: IconComponent;
  loading?: boolean;
  onClick?: () => void;
};

const UltimateButton = forwardRef<HTMLButtonElement, UltimateButtonProps>(
  (
    {
      variant = "contained",
      text,
      color = "brand",
      Icon,
      IconRight,
      loading,
      onClick,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const theme = useTheme();
    const palette = theme.palette as typeof theme.palette & {
      brand?: { primary?: string };
      brandBackground?: { secondary?: string };
      brandText?: { inverse?: string };
      brandDanger?: { primary?: string };
      brandSuccess?: { primary?: string };
    };
    const { textColor, buttonColor, borderColor } = useMemo(() => {
      if (loading || variant === "outlined") {
        const base = {
          buttonColor: palette.brandBackground?.secondary ?? "#264B9F",
          textColor: palette.brandText?.inverse ?? "#ffffff",
          borderColor: "transparent" as string,
        };
        if (color === "white")
          return { ...base, textColor: "#1B1B1B", buttonColor: "#FFF" };
        if (color === "gray")
          return { ...base, textColor: "#1B1B1B", buttonColor: "#EFEFEF" };
        if (color === "black")
          return { ...base, textColor: "#1B1B1B", borderColor: "#1B1B1B" };
        if (color === "brand")
          return {
            textColor: "#FFF",
            buttonColor: palette.brand?.primary ?? "#264B9F",
            borderColor: palette.brand?.primary ?? "#264B9F",
          };
        if (color === "brandDanger")
          return {
            textColor: palette.brandText?.inverse ?? "#ffffff",
            buttonColor: palette.brandDanger?.primary ?? "#EC1F3D",
            borderColor: palette.brandDanger?.primary ?? "#EC1F3D",
          };
        if (color === "brandSuccess")
          return {
            textColor: palette.brandText?.inverse ?? "#ffffff",
            buttonColor: palette.brandSuccess?.primary ?? "#2CB666",
            borderColor: palette.brandSuccess?.primary ?? "#2CB666",
          };
        return base;
      }

      const base = {
        textColor: palette.brandText?.inverse ?? "#ffffff",
        borderColor: "transparent" as string,
        buttonColor: palette.brand?.primary ?? "#264B9F",
      };
      if (color === "white")
        return {
          ...base,
          buttonColor: "#FFF",
          textColor: "#1B1B1B",
          borderColor: "#EBEBEB",
        };
      if (color === "gray")
        return { ...base, buttonColor: "#EFEFEF", textColor: "#1B1B1B" };
      if (color === "black") return { ...base, buttonColor: "#1B1B1B" };
      if (color === "brand") return base;
      if (color === "brandDanger")
        return {
          ...base,
          buttonColor: palette.brandDanger?.primary ?? "#EC1F3D",
        };
      if (color === "brandSuccess")
        return {
          ...base,
          buttonColor: palette.brandSuccess?.primary ?? "#2CB666",
        };
      return base;
    }, [
      loading,
      variant,
      palette.brandText?.inverse,
      palette.brand?.primary,
      palette.brandDanger?.primary,
      palette.brandSuccess?.primary,
      palette.brandBackground?.secondary,
      color,
    ]);

    return (
      <Button
        {...rest}
        ref={ref}
        variant={variant}
        sx={{
          py: 0.75,
          px: 1.5,
          bgcolor: variant === "outlined" ? "transparent" : buttonColor,
          border: "1px solid",
          borderColor: variant === "outlined" ? borderColor : "transparent",
          color: textColor,
          borderRadius: 50,
          fontWeight: 600,
          transition: "background-color 150ms ease, border-color 150ms ease",
          ...(Icon || loading ? { pr: 2 } : {}),
          ...(rest.sx ?? {}),
          ...(disabled ? { opacity: 0.3, pointerEvents: "none" } : {}),

          "&:hover": {
            ...(variant === "contained"
              ? {
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.08), rgba(0,0,0,0.08))`,
                  backgroundColor: buttonColor,
                }
              : {
                  backgroundColor: alpha(buttonColor, 0.08),
                }),
          },
          "&:active": {
            ...(variant === "contained"
              ? {
                  backgroundImage: `linear-gradient(rgba(0,0,0,0.12), rgba(0,0,0,0.12))`,
                  backgroundColor: buttonColor,
                }
              : {
                  backgroundColor: alpha(buttonColor, 0.12),
                }),
          },
          "& svg": {
            color: textColor,
            transition: "color 150ms ease-in-out",
          },
        }}
        onClick={(e) => {
          if (loading || disabled) {
            return;
          }

          onClick?.(e);
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: textColor }} />
            <Typography fontWeight="inherit" variant="textSM" color={textColor}>
              Please Wait...
            </Typography>
          </>
        ) : (
          <>
            {Icon ? (
              <Icon
                className="icon-left"
                size={24}
                style={{ marginRight: 8 }}
              />
            ) : (
              <Box sx={{ height: 24 }} />
            )}
            <Typography
              fontWeight="inherit"
              lineHeight={1}
              variant="textSM"
              color={textColor}
              sx={{
                transition: "color 150ms ease-in-out",
              }}
            >
              {text}
            </Typography>
            {IconRight ? (
              <IconRight
                className="icon-right"
                size={24}
                style={{ marginLeft: 8 }}
              />
            ) : null}
          </>
        )}
      </Button>
    );
  },
);
UltimateButton.displayName = "UltimateButton";

export default UltimateButton;
